// Panel de cuentas de Claude (22-10-2026). Qué se enseña de cada cuenta y qué dice el cartel de la pared. Las reglas
// son las del catálogo aprobado por Pau (docs/cuentas-claude/catalogo-situaciones.md); aquí solo viven como código.
// Puro: recibe la lectura de `/api/runners/usage` y la hora, y devuelve lo que se pinta.
import { t } from '../../../i18n';
import type { RunnerUsageEntry, UsageBucket, UsageLimitEntry } from '../../../lib/api';

/** Una sola raya para todo (catálogo): por debajo, «con margen»; del 80 al 99, cerca del tope; 100, agotada. */
export const RAYA = 80;
const MIN = 60_000;
/** El botón «Arréglalo» sale solo si la oficina no lo ha arreglado sola en este tiempo. */
export const BOTON_ARREGLO_MIN = 60;
/** Una llave rechazada se da por caducada si la reparación automática no la arregla en este tiempo. */
export const CADUCADA_MIN = 15;
/** Una máquina apagada más de esto se da por retirada y desaparece. */
const RETIRADA_MS = 7 * 24 * 60 * MIN;

export type Situacion = 'normal' | 'cerca' | 'agotada' | 'sin_lectura' | 'caducada' | 'impagada';
export type MotivoSinLectura = 'espera' | 'maquina' | 'lectura';

export interface Tramo {
  pct: number;
  vuelveMs: number | null;
}

export interface FilaCuenta {
  runnerId: string;
  nombre: string;
  situacion: Situacion;
  cinco: Tramo | null;
  semana: Tramo | null;
  /** Solo si se ha usado (catálogo: «Fable solo aparece si se usa»). */
  fable: Tramo | null;
  pagoPorUso: { gastado: number; limite: number | null; moneda: string | null } | null;
  /** Plan menor que Max (p. ej. «Pro»), para la nota discreta; null si es Max o no se sabe. */
  planMenor: string | null;
  motivo: MotivoSinLectura | null;
  /** Minutos sin lectura buena (o desde que falla); 0 si la última fue buena. */
  sinLecturaMin: number;
  /** Cuándo es la lectura que se enseña (para «leído hace 6 min» si no es de esta vuelta). */
  leidoMs: number | null;
  /** «conectar»: sesión caducada (solo Pau puede entrar con el correo); «arreglar»: contratar a quien lo mire. */
  boton: 'conectar' | 'arreglar' | null;
}

export type EstadoCartel = 'margen' | 'justo' | 'parado' | 'sin';

export interface Resumen {
  estado: EstadoCartel;
  libres: number;
  leidas: number;
  sinLectura: number;
  /** Cuándo vuelve la primera cuenta agotada (para «otra en 50 min»), si hace falta decirlo. */
  vuelveMs: number | null;
  /** Lo más reciente que se leyó, para «hace 40 s». */
  leidoMs: number | null;
}

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const v = Date.parse(iso);
  return Number.isFinite(v) ? v : null;
};

function tramo(b: UsageBucket | null | undefined): Tramo | null {
  if (!b) return null;
  return { pct: Math.max(0, Math.round(b.utilization)), vuelveMs: ms(b.resets_at) };
}

function limite(limits: UsageLimitEntry[] | undefined, kind: string): Tramo | null {
  const l = limits?.find((x) => x.kind === kind);
  return l ? { pct: Math.max(0, Math.round(l.percent)), vuelveMs: ms(l.resets_at) } : null;
}

/** Dada de baja (sin suscripción): no aporta nada y no sale (Pau, 22-10: «¿para qué me la vas a enseñar?»). */
export function dadaDeBaja(e: RunnerUsageEntry): boolean {
  return e.cuenta?.suscripcion === 'canceled' || e.cuenta?.plan === 'claude_free';
}

/** Anthropic no ha podido cobrarla (estados de Stripe). Sigue trabajando unos días y luego la dan de baja; mientras,
 *  el consumo da 403/429 y el motivo «espera» mentía (pauagent03, 26-10). Solo Pau puede pagar: sin «Arréglalo». */
const IMPAGO = new Set(['past_due', 'unpaid', 'incomplete']);
export function impagada(e: RunnerUsageEntry): boolean {
  return IMPAGO.has(e.cuenta?.suscripcion ?? '');
}

function nombrePlanMenor(plan: string | null | undefined): string | null {
  if (!plan || plan === 'claude_max' || plan === 'claude_free') return null;
  if (plan === 'claude_pro') return 'Pro';
  return null;
}

export function filaDeCuenta(e: RunnerUsageEntry, ahora: number): FilaCuenta | null {
  if (dadaDeBaja(e)) return null;
  const vistoMs = ms(e.lastSeen);
  if (e.status === 'offline' && (vistoMs === null || ahora - vistoMs > RETIRADA_MS)) return null;

  const nombre = e.cuenta?.nombre ?? e.hostname;
  const planMenor = nombrePlanMenor(e.cuenta?.plan);
  const okMs = ms(e.ultimaLecturaOk);
  const fallaMs = ms(e.fallandoDesde);

  if (impagada(e)) {
    return {
      runnerId: e.runnerId, nombre, situacion: 'impagada', cinco: null, semana: null, fable: null, pagoPorUso: null,
      planMenor, motivo: null, sinLecturaMin: 0, leidoMs: null, boton: null,
    };
  }

  // Un fallo suelto no hace parpadear la cuenta: el servidor guarda la última lectura buena 10 min (`usoReciente`).
  const uso = e.usage ?? e.usoReciente ?? null;
  if (uso) {
    const semana = limite(uso.limits, 'weekly_all') ?? tramo(uso.seven_day);
    // Sin bloque de 5 horas abierto, Anthropic no lo manda (medido 22-10 en agentpau06): es un 0 % sin hora de vuelta.
    const cinco = limite(uso.limits, 'session') ?? tramo(uso.five_hour) ?? (semana ? { pct: 0, vuelveMs: null } : null);
    const fableRaw = limite(uso.limits, 'weekly_scoped');
    const fable = fableRaw && fableRaw.pct > 0 ? fableRaw : null;
    const peor = Math.max(cinco?.pct ?? 0, semana?.pct ?? 0);
    const pago = uso.pago_por_uso;
    return {
      runnerId: e.runnerId,
      nombre,
      situacion: peor >= 100 ? 'agotada' : peor >= RAYA ? 'cerca' : 'normal',
      cinco,
      semana,
      fable,
      pagoPorUso: pago && pago.activo ? { gastado: pago.gastado, limite: pago.limite, moneda: pago.moneda } : null,
      planMenor,
      motivo: null,
      sinLecturaMin: 0,
      leidoMs: e.usage ? ms(e.leidoEn) : okMs,
      boton: null,
    };
  }

  const desde = fallaMs ?? okMs ?? ahora;
  const sinLecturaMin = Math.max(0, Math.floor((ahora - desde) / MIN));
  const caducada = e.usageErrorCode === 'OAUTH_TOKEN_INVALID' && sinLecturaMin >= CADUCADA_MIN;
  const motivo: MotivoSinLectura =
    e.usageErrorCode === 'RATE_LIMITED' ? 'espera' : e.usageErrorCode === 'RUNNER_OFFLINE' ? 'maquina' : 'lectura';
  return {
    runnerId: e.runnerId,
    nombre,
    situacion: caducada ? 'caducada' : 'sin_lectura',
    cinco: null,
    semana: null,
    fable: null,
    pagoPorUso: null,
    planMenor,
    motivo: caducada ? null : motivo,
    sinLecturaMin,
    leidoMs: null,
    boton: caducada ? 'conectar' : sinLecturaMin >= BOTON_ARREGLO_MIN ? 'arreglar' : null,
  };
}

/** Las cuentas a enseñar, siempre en el mismo orden (por nombre): que nada salte de sitio entre visitas. */
export function filasDeCuentas(entries: RunnerUsageEntry[], ahora: number): FilaCuenta[] {
  return entries
    .map((e) => filaDeCuenta(e, ahora))
    .filter((f): f is FilaCuenta => f !== null)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}

export function conMargen(f: FilaCuenta): boolean {
  return (f.situacion === 'normal') && (f.cinco?.pct ?? 0) < RAYA && (f.semana?.pct ?? 0) < RAYA;
}

/** El cartel: cuántas con margen de las que se pueden leer. Las que no se leen no cuentan ni a favor ni en contra. */
export function resumen(filas: FilaCuenta[], entries: RunnerUsageEntry[]): Resumen {
  const leidasF = filas.filter((f) => f.cinco || f.semana);
  const libres = leidasF.filter(conMargen).length;
  const sinLectura = filas.length - leidasF.length;
  const vueltas = leidasF
    .filter((f) => !conMargen(f))
    .map((f) => {
      const tope = [f.cinco, f.semana].filter((t): t is Tramo => !!t && t.pct >= RAYA && t.vuelveMs !== null);
      return Math.max(...tope.map((t) => t.vuelveMs as number));
    })
    .filter((v) => Number.isFinite(v));
  const leido = entries.map((e) => ms(e.leidoEn)).filter((v): v is number => v !== null);
  const estado: EstadoCartel = !leidasF.length ? 'sin' : libres >= 2 ? 'margen' : libres === 1 ? 'justo' : 'parado';
  return {
    estado,
    libres,
    leidas: leidasF.length,
    sinLectura,
    vuelveMs: estado === 'justo' || estado === 'parado' ? (vueltas.length ? Math.min(...vueltas) : null) : null,
    leidoMs: leido.length ? Math.max(...leido) : null,
  };
}

/** «en 50 min», «en 2 h 10 min» (menos de 24 h); si no, «mañana 11:00» o «dom 05:00». Hora local del navegador. */
export function cuando(vuelveMs: number | null, ahora: number): string {
  if (vuelveMs === null) return '';
  const dif = vuelveMs - ahora;
  if (dif <= 0) return '';
  if (dif < 24 * 60 * MIN) {
    const h = Math.floor(dif / (60 * MIN));
    const m = Math.round((dif % (60 * MIN)) / MIN);
    if (h === 0) return t('cuentasClaude.tiempo.enMin', { m: Math.max(1, m) });
    return m ? t('cuentasClaude.tiempo.enHMin', { h, m }) : t('cuentasClaude.tiempo.enH', { h });
  }
  const d = new Date(vuelveMs);
  const hoy = new Date(ahora);
  const manana = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1);
  const hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const esManana = d.getFullYear() === manana.getFullYear() && d.getMonth() === manana.getMonth() && d.getDate() === manana.getDate();
  const dia = esManana ? t('cuentasClaude.tiempo.manana') : (t('cuentasClaude.tiempo.dias').split(',')[d.getDay()] ?? '');
  return `${dia} ${hora}`;
}

/** «hace 40 s», «hace 3 min». */
export function hace(desdeMs: number | null, ahora: number): string {
  if (desdeMs === null) return '';
  const s = Math.max(0, Math.round((ahora - desdeMs) / 1000));
  if (s < 10) return t('cuentasClaude.tiempo.ahora');
  return s < 60 ? t('cuentasClaude.tiempo.haceS', { s }) : t('cuentasClaude.tiempo.haceMin', { m: Math.floor(s / 60) });
}

/** Color de una barra o cifra: gris con margen, naranja cerca del tope, rojo agotada. */
export function tono(pct: number): 'normal' | 'cerca' | 'agotada' {
  return pct >= 100 ? 'agotada' : pct >= RAYA ? 'cerca' : 'normal';
}
